import type { ReactElement } from "react";
import { useMemo, useRef, useState } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";
import { translate } from "app/filters/i18nFilter";
import { MultiselectDropdown } from "app/pages/sales-intelligence/common-components/dropdown/MultiselectDropdown";
import type { IWebhookTableCellProps } from "app/pages/sales-intelligence/modules/signals-api-webhook/types/cell-props";
import type SignalType from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalType";

import { filterSignalTypes } from "../../utils/filterSignalTypes";
import { StyledWebhookTableCellWrapper } from "../styles";

import { useSignalTypesOptions } from "./hooks";
import { StyledSignalTypeApplyButton } from "./styles";

type DropdownProps = {
    closePopup(): void;
};

const SignalTypesCell = ({
    name,
    index,
    value,
    onChange,
}: IWebhookTableCellProps<readonly SignalType[]>): ReactElement => {
    const dropdownRef = useRef(null);

    const clearValue = useMemo(() => filterSignalTypes(value), [value]);

    const [localValue, setLocalValue] = useState<readonly SignalType[]>(clearValue);

    const isDisabled = !localValue.length;

    const handleTypeSelection = (selected: SignalType) => {
        const newValues: readonly SignalType[] = localValue.includes(selected)
            ? localValue.filter((v) => v !== selected)
            : [...localValue, selected];

        setLocalValue(newValues);
    };

    const handleToggle = (isOpen: boolean) => {
        if (!isOpen) {
            return;
        }

        setLocalValue(value);
    };

    const handleApply = () => {
        if (isDisabled) {
            return;
        }

        onChange(index, name, localValue);

        (dropdownRef.current as unknown as DropdownProps).closePopup();
    };

    const options = useSignalTypesOptions();

    const buttonText = useMemo(() => {
        if (options.length <= clearValue.length || !clearValue.length) {
            return translate(`si.management.integration.signals-api.section.webhook.signal-types.all-signals`);
        }

        const defaultText = translate(`si.signals.group.${clearValue[0]}.text.compact`);

        if (clearValue.length > 1) {
            return `${defaultText} +${clearValue.length - 1}`;
        }

        return defaultText;
    }, [clearValue, options]);

    const renderFooterButton = () => (
        <Tooltip
            enabled={isDisabled}
            content={translate("si.management.integration.signals-api.section.webhook.apply-button.tooltip")}
            placement="top"
        >
            <div>
                <StyledSignalTypeApplyButton
                    onClick={handleApply}
                    disabled={isDisabled}
                >
                    {translate("common.apply")}
                </StyledSignalTypeApplyButton>
            </div>
        </Tooltip>
    );

    return (
        <StyledWebhookTableCellWrapper>
            <MultiselectDropdown<SignalType>
                ref={dropdownRef}
                width="100%"
                elementId={`SignalTypesCell_${name}_${index}`}
                onSelect={handleTypeSelection}
                selectedItems={localValue}
                items={options}
                buttonText={buttonText}
                buttonTextColor={colorsPalettes.carbon[400]}
                renderFooter={renderFooterButton}
                onToggle={handleToggle}
            />
        </StyledWebhookTableCellWrapper>
    );
};

export default SignalTypesCell;
